// LED控制器类 - 处理蓝牙连接和LED屏幕控制
const parse_data = require('./utils/parse_data.js');
const parse_ack = require('./utils/parse_ack.js');

// 导入 Vue 的 markRaw 函数，用于标记蓝牙对象为非响应式
// 这样可以避免 Vue 3 的 Proxy 与 Web Bluetooth API 对象的冲突
let markRaw = (obj) => obj; // 默认实现，如果 Vue 不可用则直接返回对象

// 尝试多种方式获取 markRaw
try {
    // 方式1：尝试从 Vue 模块导入（适用于构建环境）
    if (typeof require !== 'undefined') {
        try {
            const { markRaw: vueMarkRaw } = require('vue');
            if (vueMarkRaw) {
                markRaw = vueMarkRaw;
                // console.log('markRaw 已从 Vue 模块导入');
            }
        } catch (requireError) {
            // require 失败，继续尝试其他方式
        }
    }
    
    // 方式2：从全局 Vue 对象获取
    if (markRaw === ((obj) => obj)) {
        if (typeof window !== 'undefined' && window.Vue && window.Vue.markRaw) {
            markRaw = window.Vue.markRaw;
            console.log('markRaw 已从全局 Vue 对象获取');
        }
    }
} catch (e) {
    console.warn('Vue markRaw 获取失败，将使用默认实现（直接返回对象）:', e.message);
}

// console.log('markRaw 函数已配置:', typeof markRaw === 'function');

class LEDController {
    constructor() {
        this.bleDevice = null;
        this.bleServer = null;
        this.bleCharacteristicWrite = null;
        this.bleCharacteristicReceive = null;
        this.isConnecting = false;
        this.connectionStatus = '未连接';
        this.screenParams = {
            width: 0,  // 初始值设为0，等待从设备获取实际参数
            height: 0
        };
        this.brightness = 10;
        this.sno = 1;
        this.stationText = '下一站：T4候机楼站';  // 站点播报文本
        this.serviceText = '服务热线电话：968828，欢迎投诉！';  // 服务语播报文本
        this.deviceInfo = null;
        this.configData = null;
        this.section2Text = null; // 存储分区2的内容，用于保持不变
        this.currentStationChinese = ''; // 当前站点中文信息
        this.currentStationEnglish = ''; // 当前站点英文信息
        this.carouselTimer = null; // 轮播定时器
        this.currentCarouselIndex = 0; // 当前轮播索引
        this.carouselTexts = []; // 轮播文本数组
        this.bleConnectionWatchdog = null; // 蓝牙连接监控定时器
        this.everConnected = false; // 是否曾经连接过设备
        this.welcomeSent = false; // 标记是否已发送欢迎信息
        this.forceInitial32x16WelcomeScroll = false; // 仅首次连接欢迎语强制左滚
        this.onDeviceInfoUpdate = null; // 设备信息更新回调
        this.programCleared = true; // 首次连接默认视为无基础节目，发送一次完整节目后再走实时刷新
    }

    generateSno() {
        this.sno = (this.sno % 255) + 1;
        return this.sno;
    }

    // （已移除）浏览器/上下文与 Android 兼容性检查

    // 连接设备 - 这是主要连接方法
    async connect() {
        return await this.connectDevice();
    }

    async connectDevice() {
        try {
            if (this.isConnecting) return; // 如果正在连接，则不执行任何操作
            this.isConnecting = true;
            
            // 显示连接中状态
            this.connectionStatus = '正在连接...';
            
            // 请求蓝牙设备
            // 正式产品版本：仅允许 TL 开头的设备连接
            let requestOptions = {
                filters: [
                    { namePrefix: 'TL' }
                ],
                optionalServices: [0xfff0, 0xfee7]
            };

            console.log('正在请求蓝牙设备...', requestOptions);
            
            // 使用 markRaw 标记蓝牙设备对象，防止 Vue 3 Proxy 包装
            this.bleDevice = markRaw(await navigator.bluetooth.requestDevice(requestOptions));
            
            // 监听设备断开连接事件（参照项目3）
            this.bleDevice.addEventListener('gattserverdisconnected', () => {
                console.log('GATT Server 已断开连接');
                this.handleDisconnection();
            });
            
            console.log('选择的设备:', this.bleDevice.name);
            
            // 连接到GATT服务器
            this.bleServer = markRaw(await this.bleDevice.gatt.connect());
            console.log('GATT服务器已连接:', this.bleServer);
            
            // 获取服务和特征值
            let services = await this.bleServer.getPrimaryServices();
            for (let i in services) {
                let service = await this.bleServer.getPrimaryService(services[i].uuid);
                let characteristics = await service.getCharacteristics();
                console.log('特征值:', characteristics);
                for (let j in characteristics) {
                    let uuid = characteristics[j].uuid.slice(0, 8);
                    if (uuid == '0000fff2' || uuid == '0000ff02')
                        this.bleCharacteristicWrite = markRaw(await service.getCharacteristic(characteristics[j].uuid));
                    if (uuid == '0000fff1' || uuid == '0000ff01')
                        this.bleCharacteristicReceive = markRaw(await service.getCharacteristic(characteristics[j].uuid));
                }
            }
            
            // 启用通知
            if (this.bleCharacteristicReceive) {
                await this.bleCharacteristicReceive.startNotifications();
                this.bleCharacteristicReceive.addEventListener('characteristicvaluechanged', this.handleNotifications.bind(this));
            }
            
            // 立即设置为已连接状态（参照项目3的做法）
            this.connectionStatus = '已连接';
            this.everConnected = true;
            
            // 更新设备信息
            this.deviceInfo = {
                deviceName: this.bleDevice.name,
                deviceParams: null
            };
            
            // 启动连接监控定时器
            this.startConnectionWatchdog();
            
            // 异步获取设备参数（不阻塞连接流程）
            this.getScreenParams().then(() => {
                // 等待一会儿再获取亮度，避免命令冲突
                return new Promise(resolve => setTimeout(resolve, 500));
            }).then(() => {
                return this.getBrightness();
            }).catch(error => {
                console.error('获取设备参数失败:', error);
            });
            
            return true;
        } catch (error) {
            console.error('蓝牙连接错误:', error);
            this.connectionStatus = '连接失败';
            // 简化错误处理，直接抛出错误消息
            throw new Error('连接失败: ' + (error.message || '未知错误'));
        } finally {
            this.isConnecting = false;
        }
    }

    // 将技术错误转换为用户友好的错误信息
    getUserFriendlyErrorMessage(error) {
        const errorMessage = error.message.toLowerCase();
        const userAgent = navigator.userAgent.toLowerCase();
        const isAndroid = userAgent.includes('android');
        const isChrome = userAgent.includes('chrome');
        const isEdge = userAgent.includes('edge');

        // 如果是我们自定义的错误信息，直接返回
        if (error.message.includes('此浏览器不支持') || 
            error.message.includes('Android设备建议') ||
            error.message.includes('版本过低')) {
            return error.message;
        }

        // 处理常见的Web Bluetooth错误
        if (errorMessage.includes('requestdevice')) {
            if (isAndroid) {
                return `Android设备连接失败。请尝试以下解决方案：
1. 确保使用Chrome或Edge浏览器
2. 在Chrome中访问 chrome://flags 搜索并启用 "Experimental Web Platform features"
3. 确保蓝牙已开启且LED设备处于可连接状态
4. 尝试刷新页面后重新连接

原始错误：${error.message}`;
            } else {
                return `蓝牙设备请求失败。请确保：
1. 浏览器支持Web Bluetooth API
2. 网站使用HTTPS协议
3. 蓝牙已开启且LED设备处于可连接状态

原始错误：${error.message}`;
            }
        }

        if (errorMessage.includes('user cancelled') || errorMessage.includes('user canceled')) {
            return '用户取消了设备选择。请重新点击连接按钮并选择LED设备。';
        }

        if (errorMessage.includes('device not found') || errorMessage.includes('no device')) {
            return `未找到LED设备。请确保：
1. LED设备已开启且处于可连接状态
2. 设备名称以 TL、LOY 或 YS 开头
3. 设备距离足够近（建议1米内）
4. 没有其他设备正在连接该LED设备`;
        }

        if (errorMessage.includes('gatt') || errorMessage.includes('connection')) {
            return `设备连接失败。请尝试：
1. 确保LED设备电量充足
2. 重启LED设备
3. 清除浏览器缓存后重试
4. 检查设备是否被其他应用占用

原始错误：${error.message}`;
        }

        if (errorMessage.includes('secure context') || errorMessage.includes('https')) {
            return 'Web Bluetooth需要在安全环境（HTTPS）中使用。请确保网站地址以https://开头。';
        }

        // Android特殊提示
        if (isAndroid) {
            return `Android设备连接出现问题：${error.message}

建议解决方案：
1. 在Chrome中访问 chrome://flags
2. 搜索 "Experimental Web Platform features" 并启用
3. 重启Chrome浏览器
4. 确保Android系统版本6.0+且Chrome版本56+`;
        }

        // 默认错误信息
        return `LED连接失败: ${error.message}

请尝试：
1. 使用支持Web Bluetooth的浏览器（Chrome、Edge等）
2. 确保网站使用HTTPS协议
3. 检查蓝牙设备状态和距离`;
    }

    async getScreenParams() {
        const cmd = {
            cmd: {
                get: "param_dev"
            },
            sno: this.generateSno()
        };
        const buffer = parse_data.parse_data(cmd).buffer;
        await this.bleCharacteristicWrite.writeValue(buffer);
    }

    async getBrightness() {
        const cmd = {
            cmd: {
                get: "light"
            },
            sno: this.generateSno()
        };
        const buffer = parse_data.parse_data(cmd).buffer;
        await this.bleCharacteristicWrite.writeValue(buffer);
    }

    async setBrightness(value) {
        if (value < 1 || value > 15) return;
        const cmd = {
            cmd: {
                light: {
                    type: 0,
                    value_fix: value
                }
            },
            sno: this.generateSno()
        };
        const buffer = parse_data.parse_data(cmd).buffer;
        await this.bleCharacteristicWrite.writeValue(buffer);
        this.brightness = value;
    }

    async sendCommand(cmd, data = null, message = '正在发送命令...') {
        if (!this.isConnected) throw new Error('设备未连接');
        try {
            let result;
            if (data) {
                let res = [];
                res.push(data);
                result = parse_data.parse_data(cmd, res);
            } else {
                result = parse_data.parse_data(cmd);
            }
            console.log('parse_data返回结果:', result);
            if (!result) {
                throw new Error('parse_data返回null或undefined');
            }
            const packets = [];
            let p = 0;
            while (p < result.length) {
                const len = result[p + 1] * 256 + result[p];
                const subarray = new Uint8Array(result.subarray(p + 2, p + len + 2));
                packets.push(subarray);
                p += len + 2;
            }
            console.log(`需要分${packets.length}个包发送，总大小${result.length}字节`);
            for (let i = 0; i < packets.length; i++) {
                const packet = packets[i];
                console.log(`发送第${i+1}/${packets.length}包, 大小: ${packet.length}字节`);
                await this.bleCharacteristicWrite.writeValue(packet.buffer);
                console.log(`第${i+1}包发送成功`);
                await new Promise(resolve => setTimeout(resolve, 300));
            }
            console.log('命令发送成功，共' + packets.length + '包');
            return true;
        } catch (error) {
            console.error('发送命令失败:', error);
            throw error;
        }
    }

    async clearAllPrograms() {
        const cmd = {
            cmd: {
                delete: {
                    del_all: 1
                }
            },
            sno: this.generateSno()
        };
        console.log('准备发送清除节目占用命令:', cmd);
        const success = await this.sendCommand(cmd, null, '正在清除节目占用...');
        if (success) {
            const cmdDelete128 = {
                cmd: {
                    delete: {
                        del_ids: [128]
                    }
                },
                sno: this.generateSno()
            };
            console.log('准备额外清除128号节目:', cmdDelete128);
            await this.sendCommand(cmdDelete128, null, '正在额外清除128号节目...');
            console.log('节目占用清除成功，128号节目已额外清除');
        }
    }

    async sendProgram(program) {
        if (!this.isConnected) return;
        const cmd = {
            pkts_program: program,
            sno: this.generateSno()
        };
        const success = await this.sendCommand(cmd, null, '正在发送节目...');
        if (success) {
            console.log('节目发送成功');
            // 节目创建成功后，清除programCleared标志
            this.programCleared = false;
        }
    }

    async sendStationProgram(stationText, serviceText) {
        if (!this.isConnected) return;
        
        // 获取LED配置
        const ledSettings = this.configData?.ledSettings || {};
        const stationColor = parseInt(ledSettings.stationTextColor || "255");
        const serviceColor = parseInt(ledSettings.serviceTextColor || "65280");
        const stationScrollSpeed = parseInt(ledSettings.stationScrollSpeed || "10");
        const serviceScrollSpeed = parseInt(ledSettings.serviceScrollSpeed || "10");
        
        const program = {
            id_pro: 128,
            property_pro: {
                type_pro: 1,
                show_now: 1,
                play_loop: 65535,
                width: this.screenParams.width,
                height: this.screenParams.height
            }
        };
        
        if (this.screenParams.width === 96 && this.screenParams.height === 20) {
            program.list_region = [
                {
                    info_pos: {
                        x: 0,
                        y: 0,
                        w: 96,
                        h: 20
                    },
                    list_item: [{
                        type_item: "text",
                        text: stationText,
                        size: 16,
                        color: stationColor,
                        align_horizontal: 'center',
                        align_vertical: 'center',
                        ...this.getAnimationSettings(stationText, true)
                    }]
                }
            ];
        } else if (this.screenParams.height === 16) {
            program.list_region = [
                {
                    info_pos: {
                        x: 0,
                        y: 0,
                        w: this.screenParams.width,
                        h: 16
                    },
                    list_item: [{
                        type_item: "text",
                        text: stationText,
                        size: 16,
                        color: stationColor,
                        align_horizontal: 'center',
                        align_vertical: 'center',
                        ...this.getAnimationSettings(stationText, true)
                    }]
                }
            ];
        } else {
            // 32点高两行屏（128x32、160x32等）：分区宽度使用实际屏宽
            program.list_region = [
                {
                    info_pos: {
                        x: 0,
                        y: 0,
                        w: this.screenParams.width,
                        h: 16
                    },
                    list_item: [{
                        type_item: "text",
                        text: stationText,
                        size: 16,
                        color: stationColor,
                        align_horizontal: 'center',
                        align_vertical: 'center',
                        ...this.getAnimationSettings(stationText, true)
                    }]
                },
                {
                    info_pos: {
                        x: 0,
                        y: 16,
                        w: this.screenParams.width,
                        h: 16
                    },
                    list_item: [{
                        type_item: "text",
                        text: serviceText,
                        size: 16,
                        color: serviceColor,
                        align_horizontal: 'center',
                        align_vertical: 'center',
                        ...this.getAnimationSettings(serviceText, false)
                    }]
                }
            ];
        }

        const cmd = {
            pkts_program: program,
            sno: this.generateSno()
        };

        const success = await this.sendCommand(cmd, null, '正在发送报站节目...');
        if (success) {
            console.log('报站节目发送成功');
            // 节目创建成功后，清除programCleared标志
            this.programCleared = false;
        }
    }

    // 初始化两行显示屏幕的完整欢迎节目（如160*32或64*64中部32高区域，分区1和分区2都显示欢迎信息）
    async initializeWelcomeProgram(welcomeText) {
        if (!this.isConnected) return;
        
        // 获取LED配置
        const ledSettings = this.configData?.ledSettings || {};
        const stationColor = parseInt(ledSettings.stationTextColor || "255");
        const serviceColor = parseInt(ledSettings.serviceTextColor || "65280");
        const stationScrollSpeed = parseInt(ledSettings.stationScrollSpeed || "10");
        const serviceScrollSpeed = parseInt(ledSettings.serviceScrollSpeed || "10");
        
        // 存储分区2的内容
        this.section2Text = welcomeText;
        
        const program = {
            id_pro: 128,
            property_pro: {
                type_pro: 1,
                show_now: 1,
                play_loop: 65535,
                width: this.screenParams.width,
                height: this.screenParams.height
            }
        };
        
        if (this.screenParams.width === 96 && this.screenParams.height === 20) {
            program.list_region = [
                {
                    info_pos: {
                        x: 0,
                        y: 0,
                        w: 96,
                        h: 20
                    },
                    list_item: [{
                        type_item: "text",
                        text: welcomeText,
                        size: 16,
                        color: stationColor,
                        align_horizontal: 'center',
                        align_vertical: 'center',
                        ...this.getWelcomeAnimationSettings(welcomeText)
                    }]
                }
            ];
        } else if (this.screenParams.height === 16) {
            program.list_region = [
                {
                    info_pos: {
                        x: 0,
                        y: 0,
                        w: this.screenParams.width,
                        h: 16
                    },
                    list_item: [{
                        type_item: "text",
                        text: welcomeText,
                        size: 16,
                        color: stationColor,
                        align_horizontal: 'center',
                        align_vertical: 'center',
                        ...this.getWelcomeAnimationSettings(welcomeText)
                    }]
                }
            ];
        } else if (this.screenParams.width === 64 && this.screenParams.height === 64) {
            // 64*64屏幕：使用中部32像素高区域，分两行（y:16 与 y:32）
            program.list_region = [
                {
                    info_pos: {
                        x: 0,
                        y: 16,
                        w: 64,
                        h: 16
                    },
                    list_item: [{
                        type_item: "text",
                        text: welcomeText,
                        size: 16,
                        color: stationColor,
                        align_horizontal: 'center',
                        align_vertical: 'center',
                        ...this.getWelcomeAnimationSettings(welcomeText)
                    }]
                },
                {
                    info_pos: {
                        x: 0,
                        y: 32,
                        w: 64,
                        h: 16
                    },
                    list_item: [{
                        type_item: "text",
                        text: welcomeText,
                        size: 16,
                        color: serviceColor,
                        align_horizontal: 'center',
                        align_vertical: 'center',
                        ...this.getWelcomeAnimationSettings(welcomeText)
                    }]
                }
            ];
        } else {
            // 32点高两行屏（128x32、160x32等）：分区宽度使用实际屏宽
            program.list_region = [
                {
                    info_pos: {
                        x: 0,
                        y: 0,
                        w: this.screenParams.width,
                        h: 16
                    },
                    list_item: [{
                        type_item: "text",
                        text: welcomeText,
                        size: 16,
                        color: stationColor,
                        align_horizontal: 'center',
                        align_vertical: 'center',
                        ...this.getWelcomeAnimationSettings(welcomeText)
                    }]
                },
                {
                    info_pos: {
                        x: 0,
                        y: 16,
                        w: this.screenParams.width,
                        h: 16
                    },
                    list_item: [{
                        type_item: "text",
                        text: welcomeText,
                        size: 16,
                        color: serviceColor,
                        align_horizontal: 'center',
                        align_vertical: 'center',
                        ...this.getWelcomeAnimationSettings(welcomeText)
                    }]
                }
            ];
        }

        const cmd = {
            pkts_program: program,
            sno: this.generateSno()
        };

        const success = await this.sendCommand(cmd, null, '正在发送欢迎节目...');
        if (success) {
            console.log('欢迎节目发送成功');
            // 节目创建成功后，清除programCleared标志
            this.programCleared = false;
        }
    }

    // 只刷新分区1的内容（160*32屏幕中，分区2保持不变）
    async refreshSection1Only(stationText) {
        if (!this.isConnected) {
            console.error('设备未连接');
            return;
        }

        // 更新stationText
        this.stationText = stationText;

        // 对于两行显示屏幕，只刷新分区1
        if (this.isTwoRowDisplayScreen()) {
            await this.refreshTextSection(1);
        } else {
            // 对于其他尺寸屏幕，使用完整节目更新
            await this.sendStationProgram(stationText, this.serviceText);
        }
    }

    // 刷新文字节目分区
    async refreshTextSection(sectionId) {
        if (!this.isConnected) {
            console.error('设备未连接');
            return;
        }

        // 如果是96x20屏幕，且尝试刷新分区2，则直接返回
        if (this.screenParams.width === 96 && this.screenParams.height === 20 && sectionId === 2) {
            console.log('96x20屏幕不支持分区2刷新');
            return;
        }

        // 如果是16像素高度的屏幕，且尝试刷新分区2，则直接返回
        if (this.screenParams.height === 16 && sectionId === 2) {
            console.log('16像素高度屏幕不支持分区2刷新');
            return;
        }

        try {
            console.log(`准备刷新第${sectionId}区域...`);

            // 获取LED配置
            const ledSettings = this.configData?.ledSettings || {};
            const stationColor = parseInt(ledSettings.stationTextColor || "255");
            const serviceColor = parseInt(ledSettings.serviceTextColor || "65280");
            const stationScrollSpeed = parseInt(ledSettings.stationScrollSpeed || "10");
            const serviceScrollSpeed = parseInt(ledSettings.serviceScrollSpeed || "10");

            // 选择要显示的文本
            let text, color, scrollSpeed;
            if (sectionId === 1) {
                text = this.stationText;
                color = stationColor;
                scrollSpeed = stationScrollSpeed;
            } else {
                // 分区2保持原有内容（欢迎信息或服务信息）
                text = this.section2Text || this.serviceText;
                color = serviceColor;
                scrollSpeed = serviceScrollSpeed;
            }

            // 格式化文本显示（连续滚动时将换行符替换为空格）
            const formattedText = this.formatTextForDisplay(text, sectionId === 1);

            const width = this.screenParams.width;
            const height = this.screenParams.height;
            const cmd = {
                cmd: {
                    rt_show: {
                        id_pro: 128, // 固定使用128号节目
                        width: width,
                        height: height,
                        data_save: 1,
                        res_show: [
                            {
                                id_rect: sectionId,
                                list_item: [
                                    {
                                        type_item: "text",
                                        text: formattedText,
                                        size: 16,
                                        color: color,
                                        align_horizontal: 'center',
                                        align_vertical: 'center',
                                        ...this.getAnimationSettings(text, sectionId === 1) // 使用原始文本判断动画
                                    }
                                ]
                            }
                        ]
                    }
                },
                sno: this.generateSno()
            };

            console.log(`准备刷新第${sectionId}区域:`, JSON.stringify(cmd, null, 2));

            // 使用parse_data处理命令
            const result = parse_data.parse_data(cmd);
            if (!result) {
                throw new Error('parse_data返回null或undefined');
            }

            // 将result分割成多个数据包
            const packets = [];
            let p = 0;

            while (p < result.length) {
                const len = result[p + 1] * 256 + result[p];
                const subarray = new Uint8Array(result.subarray(p + 2, p + len + 2));
                packets.push(subarray);
                p += len + 2;
            }

            console.log(`需要分${packets.length}个包发送，总大小${result.length}字节`);

            // 将数据包分成20字节的小块发送
            const writePack = async (buffer) => {
                let bytes = buffer.byteLength;
                let pos = 0;

                while (bytes > 0) {
                    let tmpBuffer;
                    if (bytes > 20) {
                        tmpBuffer = buffer.slice(pos, pos + 20);
                        pos += 20;
                        bytes -= 20;
                        await this.bleCharacteristicWrite.writeValue(tmpBuffer);
                        // 短暂延迟，让设备有时间处理
                        await new Promise(resolve => setTimeout(resolve, 20));
                    } else {
                        tmpBuffer = buffer.slice(pos, pos + bytes);
                        pos += bytes;
                        bytes = 0;
                        await this.bleCharacteristicWrite.writeValue(tmpBuffer);
                    }
                }
            };

            // 逐个发送数据包
            for (let i = 0; i < packets.length; i++) {
                const packet = packets[i];
                console.log(`发送第${i+1}/${packets.length}包, 大小: ${packet.length}字节`);
                await writePack(packet.buffer);
                console.log(`第${i+1}包发送成功`);
                // 等待设备处理时间
                await new Promise(resolve => setTimeout(resolve, 300));
            }

            console.log(`第${sectionId}区域刷新成功`);
        } catch (error) {
            console.error(`刷新第${sectionId}区域失败:`, error);
            throw error;
        }
    }

    // 同时刷新两个分区（160*32屏幕专用，避免分开传输造成延迟）
    async refreshBothSections(section1Text, section2Text) {
        if (!this.isConnected) {
            console.error('设备未连接');
            return;
        }

        // 只适用于两行显示屏幕
        if (!this.isTwoRowDisplayScreen()) {
            console.log('refreshBothSections仅适用于两行显示屏幕');
            return;
        }

        // 确保文本参数不为空
        section1Text = section1Text || '';
        section2Text = section2Text || '';

        // 格式化文本显示（连续滚动时将换行符替换为空格）
        const formattedSection1Text = this.formatTextForDisplay(section1Text, true);
        const formattedSection2Text = this.formatTextForDisplay(section2Text, false);

        try {
            console.log('准备同时刷新两个分区...');

            // 获取LED配置
            const ledSettings = this.configData?.ledSettings || {};
            const stationColor = parseInt(ledSettings.stationTextColor || "255");
            const serviceColor = parseInt(ledSettings.serviceTextColor || "65280");

            // 更新内部状态
            this.stationText = formattedSection1Text;
            this.section2Text = formattedSection2Text;

            const cmd = {
                cmd: {
                    rt_show: {
                        id_pro: 128, // 固定使用128号节目
                        width: this.screenParams.width,
                        height: this.screenParams.height,
                        data_save: 1,
                        res_show: [
                            {
                                id_rect: 1,
                                list_item: [
                                    {
                                        type_item: "text",
                                        text: formattedSection1Text,
                                        size: 16,
                                        color: stationColor,
                                        align_horizontal: 'center',
                                        align_vertical: 'center',
                                        ...this.getAnimationSettings(section1Text, true) // 使用原始文本判断动画
                                    }
                                ]
                            },
                            {
                                id_rect: 2,
                                list_item: [
                                    {
                                        type_item: "text",
                                        text: formattedSection2Text,
                                        size: 16,
                                        color: serviceColor,
                                        align_horizontal: 'center',
                                        align_vertical: 'center',
                                        ...this.getAnimationSettings(section2Text, false) // 使用原始文本判断动画
                                    }
                                ]
                            }
                        ]
                    }
                },
                sno: this.generateSno()
            };

            console.log('准备同时刷新两个分区:', JSON.stringify(cmd, null, 2));

            // 使用parse_data处理命令
            const result = parse_data.parse_data(cmd);
            if (!result) {
                throw new Error('parse_data返回null或undefined');
            }

            // 将result分割成多个数据包
            const packets = [];
            let p = 0;

            while (p < result.length) {
                const len = result[p + 1] * 256 + result[p];
                const subarray = new Uint8Array(result.subarray(p + 2, p + len + 2));
                packets.push(subarray);
                p += len + 2;
            }

            console.log(`需要分${packets.length}个包发送，总大小${result.length}字节`);

            // 将数据包分成20字节的小块发送
            const writePack = async (buffer) => {
                let bytes = buffer.byteLength;
                let pos = 0;

                while (bytes > 0) {
                    let tmpBuffer;
                    if (bytes > 20) {
                        tmpBuffer = buffer.slice(pos, pos + 20);
                        pos += 20;
                        bytes -= 20;
                        await this.bleCharacteristicWrite.writeValue(tmpBuffer);
                        // 短暂延迟，让设备有时间处理
                        await new Promise(resolve => setTimeout(resolve, 20));
                    } else {
                        tmpBuffer = buffer.slice(pos, pos + bytes);
                        pos += bytes;
                        bytes = 0;
                        await this.bleCharacteristicWrite.writeValue(tmpBuffer);
                    }
                }
            };

            // 逐个发送数据包
            for (let i = 0; i < packets.length; i++) {
                const packet = packets[i];
                console.log(`发送第${i+1}/${packets.length}包, 大小: ${packet.length}字节`);
                await writePack(packet.buffer);
                console.log(`第${i+1}包发送成功`);
                // 等待设备处理时间
                await new Promise(resolve => setTimeout(resolve, 300));
            }

            console.log('两个分区同时刷新成功');
        } catch (error) {
            console.error('同时刷新两个分区失败:', error);
            throw error;
        }
    }

    // 专用于160*32屏幕的单语言分区刷新（避免清屏闪烁）
    async sendSingleLanguageToSection(text, sections = [1], programType = 'station') {
        if (!this.isConnected) {
            console.error('设备未连接');
            return;
        }

        // 仅适用于两行显示屏幕（160x32 或 64x64）
        if (!this.isTwoRowDisplayScreen()) {
            console.log('sendSingleLanguageToSection仅适用于两行显示屏幕（160x32 或 64x64）');
            return;
        }

        // 确保文本参数不为空
        text = text || '';

        // 格式化文本显示
        const formattedText = this.formatTextForDisplay(text, true);

        try {
            console.log(`准备实时刷新单语言内容到分区${sections.join(',')}（无清屏）...`);

            // 更新内部状态
            if (sections.includes(1)) {
                this.stationText = formattedText;
            }
            if (sections.includes(2)) {
                this.section2Text = formattedText;
            }

            // 构建要刷新的分区数组
            const res_show = [];
            
            // 使用辅助方法确定颜色
            const color = this.getColorByProgramType(programType);
            
            for (const sectionId of sections) {
                res_show.push({
                    id_rect: sectionId,
                    list_item: [
                        {
                            type_item: "text",
                            text: formattedText,
                            size: 16,
                            color: color,
                            align_horizontal: 'center',
                            align_vertical: 'center',
                            ...this.getAnimationSettings(text, sectionId === 1) // 使用原始文本判断动画
                        }
                    ]
                });
            }

            // 使用 rt_show 实时刷新模式，避免清屏
            const cmd = {
                cmd: {
                    rt_show: {
                        id_pro: 128, // 固定使用128号节目
                        width: this.screenParams.width,
                        height: this.screenParams.height,
                        data_save: 1, // 保存到设备缓存
                        res_show: res_show
                    }
                },
                sno: this.generateSno()
            };

            console.log(`实时刷新${programType}单语言内容:`, JSON.stringify(cmd, null, 2));

            // 使用parse_data处理命令
            const result = parse_data.parse_data(cmd);
            if (!result) {
                throw new Error('parse_data返回null或undefined');
            }

            // 将result分割成多个数据包
            const packets = [];
            let p = 0;

            while (p < result.length) {
                const len = result[p + 1] * 256 + result[p];
                const subarray = new Uint8Array(result.subarray(p + 2, p + len + 2));
                packets.push(subarray);
                p += len + 2;
            }

            console.log(`需要分${packets.length}个包发送，总大小${result.length}字节`);

            // 将数据包分成20字节的小块发送
            const writePack = async (buffer) => {
                let bytes = buffer.byteLength;
                let pos = 0;

                while (bytes > 0) {
                    let tmpBuffer;
                    if (bytes > 20) {
                        tmpBuffer = buffer.slice(pos, pos + 20);
                        pos += 20;
                        bytes -= 20;
                        await this.bleCharacteristicWrite.writeValue(tmpBuffer);
                        await new Promise(resolve => setTimeout(resolve, 20));
                    } else {
                        tmpBuffer = buffer.slice(pos, pos + bytes);
                        pos += bytes;
                        bytes = 0;
                        await this.bleCharacteristicWrite.writeValue(tmpBuffer);
                    }
                }
            };

            // 逐个发送数据包
            for (let i = 0; i < packets.length; i++) {
                const packet = packets[i];
                console.log(`发送第${i+1}/${packets.length}包, 大小: ${packet.length}字节`);
                await writePack(packet.buffer);
                console.log(`第${i+1}包发送成功`);
                await new Promise(resolve => setTimeout(resolve, 300));
            }

            console.log(`${programType}单语言内容实时刷新成功，无清屏闪烁`);
        } catch (error) {
            console.error(`发送${programType}单语言内容失败:`, error);
            throw error;
        }
    }

    // 便捷方法：只刷新分区1（用于单语言进站/出站）
    async refreshSection1Only(text, programType = 'station') {
        return this.sendSingleLanguageToSection(text, [1], programType);
    }

    // 便捷方法：只刷新分区2（用于单语言服务语）
    async refreshSection2Only(text, programType = 'service') {
        return this.sendSingleLanguageToSection(text, [2], programType);
    }

    // 便捷方法：同时刷新分区1和2（用于单语言欢迎页面）
    async refreshBothSections(text, programType = 'welcome') {
        return this.sendSingleLanguageToSection(text, [1, 2], programType);
    }

    // 专用于小屏幕的实时文字刷新（96×20和16点阵）
    async refreshSmallScreenText(text, programType = 'station') {
        if (!this.isConnected) {
            console.error('设备未连接');
            return;
        }

        // 只适用于小屏幕
        if (!(this.screenParams.height === 16 || this.screenParams.height === 20)) {
            console.log('refreshSmallScreenText仅适用于16成20点阵屏幕');
            return;
        }

        // 确保文本参数不为空
        text = text || '';

        // 根据programType判断是否为站点信息
        const isStation = !(programType === 'service' || (typeof programType === 'string' && programType.includes('服务语')));
        console.log(`小屏幕刷新 - 程序类型: ${programType}, 是否站点: ${isStation}`);

        // 格式化文本显示（处理换行符，特别是当noscrollmode为left时）
        const formattedText = this.formatTextForDisplay(text, isStation);

        try {
            console.log(`准备实时刷新小屏幕内容: ${formattedText.substring(0, 20)}...`);

            // 更新内部状态
            this.stationText = formattedText;
            
            // 使用辅助方法确定颜色
            const color = this.getColorByProgramType(programType);
            console.log(`小屏幕使用颜色: ${color}, 程序类型: ${programType}`);

            // 使用 rt_show 实时刷新模式，避免清屏
            const cmd = {
                cmd: {
                    rt_show: {
                        id_pro: 128, // 固定使用128号节目
                        width: this.screenParams.width,
                        height: this.screenParams.height,
                        data_save: 1, // 保存到设备缓存
                        res_show: [
                            {
                                id_rect: 1, // 小屏幕使用分区1
                                list_item: [
                                    {
                                        type_item: "text",
                                        text: formattedText,
                                        size: 16,
                                        color: color, // 使用根据程序类型确定的颜色
                                        align_horizontal: 'center',
                                        align_vertical: 'center',
                                        ...this.getAnimationSettings(text, isStation) // 使用原始文本和正确的isStation参数判断动画设置
                                    }
                                ]
                            }
                        ]
                    }
                },
                sno: this.generateSno()
            };

            console.log(`实时刷新小屏幕${programType}内容:`, JSON.stringify(cmd, null, 2));

            // 使用parse_data处理命令
            const result = parse_data.parse_data(cmd);
            if (!result) {
                throw new Error('parse_data返回null或undefined');
            }

            // 将result分割成多个数据包
            const packets = [];
            let p = 0;

            while (p < result.length) {
                const len = result[p + 1] * 256 + result[p];
                const subarray = new Uint8Array(result.subarray(p + 2, p + len + 2));
                packets.push(subarray);
                p += len + 2;
            }

            console.log(`需要分${packets.length}个包发送，总大小${result.length}字节`);

            // 逐个发送数据包
            for (let i = 0; i < packets.length; i++) {
                const packet = packets[i];
                console.log(`发送第${i+1}/${packets.length}包, 大小: ${packet.length}字节`);
                await this.bleCharacteristicWrite.writeValue(packet.buffer);
                console.log(`第${i+1}包发送成功`);
                await new Promise(resolve => setTimeout(resolve, 300));
            }

            console.log(`小屏幕${programType}内容实时刷新成功`);
        } catch (error) {
            console.error(`发送小屏幕${programType}内容失败:`, error);
            throw error;
        }
    }

    // 专用于160*32屏幕的实时双语刷新（避免清屏闪烁，一次性更新两个分区）
    async sendDualLanguageProgram(chineseText, englishText, programType = 'station') {
        if (!this.isConnected) {
            console.error('设备未连接');
            return;
        }

        // 仅适用于两行显示屏幕（160x32 或 64x64）
        if (!this.isTwoRowDisplayScreen()) {
            console.log('sendDualLanguageProgram仅适用于两行显示屏幕（160x32 或 64x64）');
            return;
        }

        // 确保文本参数不为空
        chineseText = chineseText || '';
        englishText = englishText || '';

        // 格式化文本显示（连续滚动时将换行符替换为空格）
        const formattedChineseText = this.formatTextForDisplay(chineseText, true);
        const formattedEnglishText = this.formatTextForDisplay(englishText, false);

        try {
            console.log('准备实时刷新双语内容（无清屏）...');

            // 更新内部状态
            this.stationText = formattedChineseText;
            this.section2Text = formattedEnglishText;
            
            // 获取LED配置
            const ledSettings = this.configData?.ledSettings || {};
            const stationColor = parseInt(ledSettings.stationTextColor || "255");
            const serviceColor = parseInt(ledSettings.serviceTextColor || "65280");
            
            // 检查是否为服务语消息
            const isServiceMessage = programType === 'service' || (typeof programType === 'string' && programType.includes('服务语'));
            
            console.log(`双语消息 - 程序类型: ${programType}, 是否服务语: ${isServiceMessage}`);
            console.log(`配置的站名颜色: ${stationColor}, 服务语颜色: ${serviceColor}`);

            // 使用 rt_show 实时刷新模式，避免清屏
            const cmd = {
                cmd: {
                    rt_show: {
                        id_pro: 128, // 固定使用128号节目
                        width: this.screenParams.width,
                        height: this.screenParams.height,
                        data_save: 1, // 保存到设备缓存
                        res_show: [
                            {
                                id_rect: 1, // 分区1
                                list_item: [
                                    {
                                        type_item: "text",
                                        text: formattedChineseText,
                                        size: 16,
                                        color: stationColor, // 分区1始终使用站点颜色
                                        align_horizontal: 'center',
                                        align_vertical: 'center',
                                        ...this.getAnimationSettings(chineseText, true) // 使用原始文本判断动画
                                    }
                                ]
                            },
                            {
                                id_rect: 2, // 分区2
                                list_item: [
                                    {
                                        type_item: "text",
                                        text: formattedEnglishText,
                                        size: 16,
                                        color: isServiceMessage ? serviceColor : stationColor, // 如果是服务语消息，分区2使用服务语颜色，否则使用站点颜色
                                        align_horizontal: 'center',
                                        align_vertical: 'center',
                                        ...this.getAnimationSettings(englishText, false) // 使用原始文本判断动画
                                    }
                                ]
                            }
                        ]
                    }
                },
                sno: this.generateSno()
            };

            console.log(`实时刷新${programType}双语内容:`, JSON.stringify(cmd, null, 2));

            // 使用parse_data处理命令
            const result = parse_data.parse_data(cmd);
            if (!result) {
                throw new Error('parse_data返回null或undefined');
            }

            // 将result分割成多个数据包
            const packets = [];
            let p = 0;

            while (p < result.length) {
                const len = result[p + 1] * 256 + result[p];
                const subarray = new Uint8Array(result.subarray(p + 2, p + len + 2));
                packets.push(subarray);
                p += len + 2;
            }

            console.log(`需要分${packets.length}个包发送，总大小${result.length}字节`);

            // 将数据包分成20字节的小块发送
            const writePack = async (buffer) => {
                let bytes = buffer.byteLength;
                let pos = 0;

                while (bytes > 0) {
                    let tmpBuffer;
                    if (bytes > 20) {
                        tmpBuffer = buffer.slice(pos, pos + 20);
                        pos += 20;
                        bytes -= 20;
                        await this.bleCharacteristicWrite.writeValue(tmpBuffer);
                        // 短暂延迟，让设备有时间处理
                        await new Promise(resolve => setTimeout(resolve, 20));
                    } else {
                        tmpBuffer = buffer.slice(pos, pos + bytes);
                        pos += bytes;
                        bytes = 0;
                        await this.bleCharacteristicWrite.writeValue(tmpBuffer);
                    }
                }
            };

            // 逐个发送数据包
            for (let i = 0; i < packets.length; i++) {
                const packet = packets[i];
                console.log(`发送第${i+1}/${packets.length}包, 大小: ${packet.length}字节`);
                await writePack(packet.buffer);
                console.log(`第${i+1}包发送成功`);
                // 等待设备处理时间
                await new Promise(resolve => setTimeout(resolve, 300));
            }

            console.log(`${programType}双语内容实时刷新成功，无清屏闪烁`);
        } catch (error) {
            console.error('实时刷新双语内容失败:', error);
            throw error;
        }
    }

    // 两行显示屏幕的多语言刷新（3-5种语言）：分区1显示第1语言，分区2显示其余语言（按换行或空格拼接，沿用滚动判定）
    async sendMultiLanguageProgram(texts, programType = 'station') {
        if (!this.isConnected) {
            console.error('设备未连接');
            return;
        }

        if (!this.isTwoRowDisplayScreen()) {
            console.log('sendMultiLanguageProgram仅适用于两行显示屏幕（160x32 或 64x64）');
            return;
        }

        if (!Array.isArray(texts) || texts.length === 0) {
            console.log('sendMultiLanguageProgram: 文本数组为空');
            return;
        }

        const firstLang = texts[0] || '';
        const restLangs = texts.slice(1);

        // 将其余语言拼接为多行或滚动一行
        // 使用与服务语拼接一致的策略：若任一行超过限制或配置left，则用空格拼；否则用\n拼
        let section2Combined;
        {
            // 构造临时结构以复用 concatenateServiceTexts 的逻辑
            const tmp = restLangs
                .filter(t => !!t && t.trim())
                .map(t => ({ language: 'other', code: 'x', text: t.trim(), fieldName: '' }));
            section2Combined = this.concatenateServiceTexts(tmp);
        }

        // 获取配置颜色
        const ledSettings = this.configData?.ledSettings || {};
        const stationColor = parseInt(ledSettings.stationTextColor || '255');
        const serviceColor = parseInt(ledSettings.serviceTextColor || '65280');
        const isServiceMessage = programType === 'service' || (typeof programType === 'string' && programType.includes('服务语'));

        // 更新状态
        this.stationText = firstLang;
        this.section2Text = section2Combined;

        // 分别格式化（保持与160x32一致：先判断是否需要滚动，再替换换行）
        const formattedFirst = this.formatTextForDisplay(firstLang, true);
        const formattedSecond = this.formatTextForDisplay(section2Combined, false);

        try {
            const cmd = {
                cmd: {
                    rt_show: {
                        id_pro: 128,
                        width: this.screenParams.width,
                        height: this.screenParams.height,
                        data_save: 1,
                        res_show: [
                            {
                                id_rect: 1,
                                list_item: [
                                    {
                                        type_item: 'text',
                                        text: formattedFirst,
                                        size: 16,
                                        color: stationColor,
                                        align_horizontal: 'center',
                                        align_vertical: 'center',
                                        ...this.getAnimationSettings(firstLang, true)
                                    }
                                ]
                            },
                            {
                                id_rect: 2,
                                list_item: [
                                    {
                                        type_item: 'text',
                                        text: formattedSecond,
                                        size: 16,
                                        color: isServiceMessage ? serviceColor : stationColor,
                                        align_horizontal: 'center',
                                        align_vertical: 'center',
                                        ...this.getAnimationSettings(section2Combined, false)
                                    }
                                ]
                            }
                        ]
                    }
                },
                sno: this.generateSno()
            };

            console.log(`实时刷新${programType}多语言内容（${texts.length}种语言）:`, JSON.stringify(cmd, null, 2));

            const result = parse_data.parse_data(cmd);
            if (!result) {
                throw new Error('parse_data返回null或undefined');
            }

            const packets = [];
            let p = 0;
            while (p < result.length) {
                const len = result[p + 1] * 256 + result[p];
                const subarray = new Uint8Array(result.subarray(p + 2, p + len + 2));
                packets.push(subarray);
                p += len + 2;
            }

            const writePack = async (buffer) => {
                let bytes = buffer.byteLength;
                let pos = 0;
                while (bytes > 0) {
                    let tmpBuffer;
                    if (bytes > 20) {
                        tmpBuffer = buffer.slice(pos, pos + 20);
                        pos += 20;
                        bytes -= 20;
                        await this.bleCharacteristicWrite.writeValue(tmpBuffer);
                        await new Promise(resolve => setTimeout(resolve, 20));
                    } else {
                        tmpBuffer = buffer.slice(pos, pos + bytes);
                        pos += bytes;
                        bytes = 0;
                        await this.bleCharacteristicWrite.writeValue(tmpBuffer);
                    }
                }
            };

            for (let i = 0; i < packets.length; i++) {
                const packet = packets[i];
                console.log(`发送第${i + 1}/${packets.length}包, 大小: ${packet.length}字节`);
                await writePack(packet.buffer);
                console.log(`第${i + 1}包发送成功`);
                await new Promise(resolve => setTimeout(resolve, 300));
            }

            console.log(`${programType}多语言内容实时刷新成功，无清屏闪烁`);
        } catch (error) {
            console.error('实时刷新多语言内容失败:', error);
            throw error;
        }
    }

    // 处理设备通知
    handleNotifications(event) {
        const value = event.target.value;
        const data = new Uint8Array(value.buffer);
        
        try {
            const result = parse_ack.parse_ack(data);
            console.log('收到设备响应:', result);

            if (result && result.ack) {
                if (result.ack.param_dev) {
                    console.log('收到屏幕参数:', result.ack.param_dev);
                    this.screenParams = {
                        width: result.ack.param_dev.width,
                        height: result.ack.param_dev.height
                    };
                    console.log('更新后的屏幕参数:', this.screenParams);
                    
                    // 更新设备信息
                    if (this.deviceInfo) {
                        this.deviceInfo.deviceParams = this.screenParams;
                        console.log('设备信息已更新:', this.deviceInfo);
                    }
                    
                    // 通知外部（App.vue）设备信息已更新
                    if (this.onDeviceInfoUpdate && typeof this.onDeviceInfoUpdate === 'function') {
                        console.log('触发设备信息更新回调');
                        this.onDeviceInfoUpdate();
                    }
                    
                    // 收到屏幕参数后，如果还没发送欢迎信息，则发送
                    if (!this.welcomeSent && this.isConnected) {
                        this.welcomeSent = true;
                        this.forceInitial32x16WelcomeScroll = this.isSpecial32x16Screen();
                        console.log('屏幕参数已获取，准备发送欢迎信息');
                        
                        // 延迟1秒后发送欢迎信息，确保设备准备好
                        setTimeout(() => {
                            this.sendWelcomeMessage('up').then(() => {
                                console.log('欢迎信息发送成功');
                            }).catch(error => {
                                console.error('发送欢迎信息失败:', error);
                            });
                        }, 1000);
                    }
                }
            }
        } catch (error) {
            console.error('处理通知数据失败:', error);
        }
    }

    // 检查是否已连接（简化判断，参照项目3）
    get isConnected() {
        // 只检查蓝牙连接状态和连接状态字符串，不强制要求屏幕参数
        return this.bleServer && this.bleServer.connected && this.connectionStatus === '已连接';
    }

    // 判断是否为两行显示屏（32点高屏如128x32/160x32，或64x64中部32高区域）
    isTwoRowDisplayScreen() {
        return (
            (this.screenParams.height === 32) ||
            (this.screenParams.width === 64 && this.screenParams.height === 64)
        );
    }

    // 获取两行显示屏的区域配置（64x64使用中部32高区域：y=16与y=32；128x32/160x32等使用顶部与y=16，宽度取实际屏宽）
    getTwoRowRegionConfig() {
        const is64x64 = this.screenParams.width === 64 && this.screenParams.height === 64;
        const topOffset = is64x64 ? 16 : 0;
        const regionWidth = this.screenParams.width;
        const rowHeight = 16;
        return { topOffset, regionWidth, rowHeight, screenWidth: this.screenParams.width, screenHeight: this.screenParams.height };
    }

    isSpecial32x16Screen() {
        return this.screenParams.width === 32 && this.screenParams.height === 16;
    }

    async sendSpecial32x16Content(text, programType) {
        console.log(`32x16特殊发送模式: 直接重发完整节目, 程序类型: ${programType}`);
        await this.sendSmallScreenFullProgram(text, programType);
    }

    // 启动蓝牙连接监控（参照项目3）
    startConnectionWatchdog() {
        // 先清除现有定时器
        this.stopConnectionWatchdog();
        
        // 启动新定时器，每5秒检查一次连接状态
        this.bleConnectionWatchdog = setInterval(() => {
            if (this.bleServer && this.bleServer.connected === false) {
                console.log('检测到设备已断开连接');
                this.handleDisconnection();
            }
        }, 5000);
        
        console.log('连接监控已启动');
    }
    
    // 停止蓝牙连接监控
    stopConnectionWatchdog() {
        if (this.bleConnectionWatchdog) {
            clearInterval(this.bleConnectionWatchdog);
            this.bleConnectionWatchdog = null;
            console.log('连接监控已停止');
        }
    }
    
    // 处理设备意外断开连接（参照项目3）
    handleDisconnection() {
        console.log('处理设备断开连接');
        
        // 停止监控
        this.stopConnectionWatchdog();
        
        // 停止轮播
        this.stopCarousel();
        
        // 更新连接状态
        this.connectionStatus = '已断开';
        
        // 清除蓝牙连接对象，但保留其他状态
        this.bleServer = null;
        this.bleCharacteristicWrite = null;
        this.bleCharacteristicReceive = null;
        
        // 重置欢迎信息标志，下次连接时重新发送
        this.welcomeSent = false;
        this.forceInitial32x16WelcomeScroll = false;
        
        // 保留 deviceInfo、configData 和其他配置，让用户可以重新连接
        // 不清除 everConnected 标志，保持UI显示
    }
    
    // 断开连接
    async disconnect() {
        // 停止监控
        this.stopConnectionWatchdog();
        
        // 停止轮播
        this.stopCarousel();
        
        if (this.bleServer && this.bleServer.connected) {
            this.bleServer.disconnect();
        }
        this.connectionStatus = '未连接';
        this.deviceInfo = null;
        this.isConnecting = false;
        
        // 重置欢迎信息标志
        this.welcomeSent = false;
        this.forceInitial32x16WelcomeScroll = false;
    }

    // 获取设备信息（返回纯数据对象，不包含蓝牙对象引用）
    getDeviceInfo() {
        // 简化实现，返回基本信息，不强制要求 deviceParams 存在
        // 即使没有获取到屏幕参数，也返回设备名称
        return {
            deviceName: this.bleDevice?.name || this.deviceInfo?.deviceName || '',
            deviceParams: this.screenParams.width > 0 ? {
                width: this.screenParams.width,
                height: this.screenParams.height
            } : null
        };
    }

    // 设置LED配置
    setLEDConfig(configData) {
        this.configData = configData;
    }

    // 清除屏幕
    async clearScreen() {
        const result = await this.clearAllPrograms();
        // 标记节目已被清除，下次发送内容时需要重建完整节目
        this.programCleared = true;
        console.log('清屏完成，已设置programCleared标志');
        return result;
    }

    // 处理LED内容中的占位符（支持多语言）
    replaceLEDPlaceholders(text, stationName, currentDirection, stationData = null, stationIndex = null) {
        if (!text || !this.configData) return text;
        
        let result = text;
        
        // 替换【当前站】(仅当stationName不为空时)
        if (stationName) {
            result = result.replace(/【当前站】/g, stationName);
        }
        
        // 替换多语言占位符【当前站(英文)】等
        if (stationData) {
            // 处理当前站的多语言占位符
            if (stationData.name_en) {
                result = result.replace(/【当前站\(英文\)】/g, stationData.name_en);
            }
            // 可以扩展其他语言，比如【当前站(日文)】等
        }
        
        // 获取当前方向的站点列表
        const stations = currentDirection === 'up' 
            ? this.configData.upDirectionStations 
            : (this.configData.downDirectionStations || []);
            
        if (stations && stations.length > 0) {
            // 替换【起点站】
            const firstStation = stations[0];
            result = result.replace(/【起点站】/g, firstStation.name);
            
            // 替换【终点站】
            const lastStation = stations[stations.length - 1];
            result = result.replace(/【终点站】/g, lastStation.name);
            
            // 替换【前方站】(下一站)
            if (stationIndex !== null && stationIndex >= 0 && stationIndex < stations.length - 1) {
                const nextStation = stations[stationIndex + 1];
                result = result.replace(/【前方站】/g, nextStation.name);
                // 替换多语言前方站
                if (nextStation.name_en) {
                    result = result.replace(/【前方站\(英文\)】/g, nextStation.name_en);
                }
            }
            
            // 替换多语言起点站和终点站
            if (firstStation.name_en) {
                result = result.replace(/【起点站\(英文\)】/g, firstStation.name_en);
            }
            if (lastStation.name_en) {
                result = result.replace(/【终点站\(英文\)】/g, lastStation.name_en);
            }
        }
        
        return result;
    }

    // 检测当前配置的语言设置
    getLanguageConfig() {
        if (!this.configData || !this.configData.languageSettings) {
            // 如果没有语言配置，默认为单语言（中文）
            return {
                isMultiLanguage: false,
                languageCount: 1,
                languages: [{ name: '中文', code: 'zh' }]
            };
        }
        
        const languageSettings = this.configData.languageSettings;
        const languageCount = languageSettings.count || (languageSettings.languages ? languageSettings.languages.length : 1);
        const isMultiLanguage = languageCount > 1;
        
        return {
            isMultiLanguage,
            languageCount,
            languages: languageSettings.languages || [{ name: '中文', code: 'zh' }]
        };
    }

    // 获取多语言文本数组（支持最多5种语言，只发送存在的语言）
    getMultiLanguageTexts(baseText, baseText_en = null, stationName = '', currentDirection = 'up', stationData = null, contentType = 'station', stationIndex = null) {
        const texts = [];
        const languageConfig = this.getLanguageConfig();
        
        // 第一语言（中文，必选）
        if (baseText) {
            const chineseText = this.replaceLEDPlaceholders(baseText, stationName, currentDirection, stationData, stationIndex);
            texts.push(chineseText);
        }
        
        // 如果是多语言配置，处理其他语言（可选）
        if (languageConfig.isMultiLanguage && languageConfig.languages && languageConfig.languages.length > 1) {
            // 从第二种语言开始处理（第一种已经是中文）
            for (let i = 1; i < languageConfig.languages.length && i < 5; i++) {
                const language = languageConfig.languages[i];
                let languageText = '';
                
                // 根据语言代码和内容类型动态查找对应的文本字段
                if (language.code === 'en' && baseText_en) {
                    // 英文特殊处理，使用传入的baseText_en参数
                    languageText = baseText_en;
                } else {
                    // 对于其他语言，动态查找对应的字段
                    languageText = this.findLanguageText(contentType, language.code, baseText);
                }
                
                // 只有当找到对应语言的文本时才添加
                if (languageText && languageText.trim()) {
                    const processedText = this.replaceLEDPlaceholders(languageText, stationName, currentDirection, stationData, stationIndex);
                    texts.push(processedText);
                }
            }
        }
        
        return texts;
    }

    // 动态查找语言文本的辅助方法
    findLanguageText(contentType, languageCode, fallbackText = '') {
        if (!this.configData || !languageCode) {
            return '';
        }

        // 根据内容类型构建可能的字段名称
        const possibleFields = [];
        
        if (contentType === 'station') {
            // 进站/出站信息
            possibleFields.push(`arrivalLEDContent_${languageCode}`);
            possibleFields.push(`departureLEDContent_${languageCode}`);
        } else if (contentType === 'welcome') {
            // 欢迎信息
            possibleFields.push(`welcomeLEDContent_${languageCode}`);
        } else if (contentType.startsWith('service')) {
            // 服务语信息，例如service1LEDContent_fz
            possibleFields.push(`${contentType}LEDContent_${languageCode}`);
        }
        
        // 通用字段名称
        possibleFields.push(`baseText_${languageCode}`);
        possibleFields.push(`ledContent_${languageCode}`);
        
        // 查找第一个存在的字段
        for (const fieldName of possibleFields) {
            if (this.configData[fieldName] && this.configData[fieldName].trim()) {
                console.log(`找到语言${languageCode}的文本字段: ${fieldName}`);
                return this.configData[fieldName];
            }
        }
        
        console.log(`未找到语言${languageCode}的文本字段，尝试的字段: ${possibleFields.join(', ')}`);
        return ''; // 不使用后备文本，只发送存在的语言
    }

    // 获取服务语的所有已定义语言文本
    getServiceLanguageTexts(serviceNumber, languageConfig, stationName = '', currentDirection = 'up', stationData = null) {
        const availableTexts = [];
        const settings = this.configData.ledSettings;
        
        if (!settings || !languageConfig || !languageConfig.languages) {
            return availableTexts;
        }
        
        // 遍历所有配置的语言
        for (const language of languageConfig.languages) {
            let fieldName;
            let languageText = '';
            
            if (language.code === 'zh') {
                // 中文（第一语言）
                fieldName = `service${serviceNumber}LEDContent`;
            } else {
                // 其他语言
                fieldName = `service${serviceNumber}LEDContent_${language.code}`;
            }
            
            if (settings[fieldName] && settings[fieldName].trim()) {
                languageText = settings[fieldName].trim();
                // 处理占位符 - 使用传入的站点名称、方向和站点数据
                languageText = this.replaceLEDPlaceholders(languageText, stationName, currentDirection, stationData);
                
                availableTexts.push({
                    language: language.name,
                    code: language.code,
                    text: languageText,
                    fieldName: fieldName
                });
                
                console.log(`找到服务语${serviceNumber}的${language.name}内容: ${languageText}`);
            } else {
                console.log(`服务语${serviceNumber}没有找到${language.name}(${language.code})的内容，字段: ${fieldName}`);
            }
        }
        
        return availableTexts;
    }

    // 根据滚动逻辑连接服务语文本
    concatenateServiceTexts(availableTexts) {
        if (!availableTexts || availableTexts.length === 0) {
            return '';
        }
        
        if (availableTexts.length === 1) {
            return availableTexts[0].text;
        }
        
        // 获取LED配置中的滚动设置
        const ledSettings = this.configData?.ledSettings || {};
        const serviceNoScrollMode = ledSettings.serviceNoScrollMode || "center";
        
        console.log(`服务语滚动模式: ${serviceNoScrollMode}`);
        
        // 如果是left模式，直接用空格连接所有文本（全域滚动）
        if (serviceNoScrollMode === "left") {
            const concatenated = availableTexts.map(item => item.text).join(' ');
            console.log(`使用left模式，用空格连接: ${concatenated}`);
            return concatenated;
        }
        
        // 对于still或top模式，需要判断每个内容是否超过屏幕显示字数上限
        const maxChars = this.getMaxDisplayChars();
        let hasOverflowText = false;
        
        // 检查是否有文本超过显示限制
        for (const item of availableTexts) {
            if (this.getTextDisplayLength(item.text) > maxChars) {
                hasOverflowText = true;
                break;
            }
        }
        
        if (hasOverflowText) {
            // 有文本超过限制，用空格分开并作为一整串文字发送
            const concatenated = availableTexts.map(item => item.text).join(' ');
            console.log(`有文本超过限制，用空格连接: ${concatenated}`);
            return concatenated;
        } else {
            // 所有文本都未超过限制，用\n连接
            const concatenated = availableTexts.map(item => item.text).join('\n');
            console.log(`所有文本都未超过限制，用\\n连接: ${concatenated}`);
            return concatenated;
        }
    }

    // 获取屏幕最大显示字符数
    getMaxDisplayChars() {
        if (this.screenParams.width === 96 && this.screenParams.height === 20) {
            return 6;
        }
        return Math.floor(this.screenParams.width / 16);
    }

    // 获取文本的显示长度（考虑全角和半角字符）
    getTextDisplayLength(text) {
        let length = 0;
        for (let i = 0; i < text.length; i++) {
            if (text.charCodeAt(i) > 127) {
                length += 1; // 全角字符
            } else {
                length += 0.5; // 半角字符
            }
        }
        return length;
    }

    // 获取当前站点的多语言内容（用于服务语显示时的分区1）
    getCurrentStationMultiLanguageText() {
        // 获取语言配置
        const languageConfig = this.getLanguageConfig();
        
        if (!languageConfig.isMultiLanguage || !languageConfig.languages) {
            // 单语言模式，返回中文站点信息
            return this.currentStationChinese || this.stationText || '当前站点';
        }
        
        // 多语言模式，收集所有可用的站点语言内容
        const availableStationTexts = [];
        
        // 中文站点信息（必选）
        const chineseStationText = this.currentStationChinese || this.stationText || '当前站点';
        if (chineseStationText) {
            availableStationTexts.push({
                language: '中文',
                code: 'zh',
                text: chineseStationText
            });
        }
        
        // 英文站点信息（如果有）
        const englishStationText = this.currentStationEnglish || '';
        if (englishStationText && englishStationText.trim() && englishStationText !== chineseStationText) {
            availableStationTexts.push({
                language: '英文',
                code: 'en',
                text: englishStationText
            });
        }
        
        // 其他语言站点信息（如果有存储）
        // 注意：由于站点信息通常在报站时已经处理，这里主要使用已经存储的中英文内容
        // 如果未来需要支持更多语言的站点信息，可以在这里扩展
        
        console.log(`找到${availableStationTexts.length}种语言的站点内容:`, availableStationTexts);
        
        // 如果只有一种语言，直接返回
        if (availableStationTexts.length === 1) {
            return availableStationTexts[0].text;
        }
        
        // 多种语言，使用与服务语相同的连接逻辑
        return this.concatenateServiceTexts(availableStationTexts);
    }

    // 停止轮播
    stopCarousel() {
        if (this.carouselTimer) {
            clearInterval(this.carouselTimer);
            this.carouselTimer = null;
        }
    }

    // 开始轮播显示（用于16/20点阵屏幕）
    async startCarousel(texts, sectionId = 1, intervalMs = 3000) {
        if (!texts || texts.length === 0) return;
        
        // 停止之前的轮播
        this.stopCarousel();
        
        this.carouselTexts = texts;
        this.currentCarouselIndex = 0;
        
        // 立即显示第一个文本
        await this.displayCarouselText(texts[0], sectionId);
        
        // 如果只有一个文本，不需要轮播
        if (texts.length === 1) return;
        
        // 开始定时轮播
        this.carouselTimer = setInterval(async () => {
            this.currentCarouselIndex = (this.currentCarouselIndex + 1) % texts.length;
            await this.displayCarouselText(texts[this.currentCarouselIndex], sectionId);
        }, intervalMs);
        
        console.log(`开始轮播显示，共${texts.length}个文本，间隔${intervalMs}ms`);
    }

    // 显示轮播文本（仅用于96*20等小屏幕，160*32屏幕已改用一次性发送）
    async displayCarouselText(text, sectionId) {
        if (!text) return;
        
        try {
            // 两行显示屏幕（160x32或64x64中部32高）已改用一次性发送，不再使用轮播
            if (this.isTwoRowDisplayScreen()) {
                console.log('两行显示屏幕已改用一次性发送，不执行轮播操作');
                return;
            }

            if (sectionId === 1) {
                this.stationText = text;
            } else {
                this.section2Text = text;
            }
            
            await this.refreshTextSection(sectionId);
        } catch (error) {
            console.error('显示轮播文本失败:', error);
        }
    }

    // 发送站点信息
    async sendStationInfo(stationName, isArrival, stationData = null, currentDirection = 'up', stationIndex = null) {
        if (!this.isConnected) {
            throw new Error('设备未连接');
        }

        // 停止可能存在的轮播
        this.stopCarousel();

        // 从压缩包JSON配置中读取LED显示内容
        if (!this.configData || !this.configData.ledSettings) {
            throw new Error('未找到LED配置，请先上传包含LED配置的压缩包');
        }
        
        const settings = this.configData.ledSettings;
        let baseText = '';
        let baseText_en = '';
        
        if (isArrival) {
            // 进站信息 - 优先使用站点自定义内容，否则使用默认模板
            if (stationData && stationData.arrivalLEDContent && stationData.arrivalLEDContent.trim()) {
                baseText = stationData.arrivalLEDContent;
                baseText_en = stationData.arrivalLEDContent_en || '';
            } else {
                baseText = settings.arrivalLEDContent;
                baseText_en = settings.arrivalLEDContent_en || '';
            }
        } else {
            // 出站信息 - 优先使用站点自定义内容，否则使用默认模板
            if (stationData && stationData.departureLEDContent && stationData.departureLEDContent.trim()) {
                baseText = stationData.departureLEDContent;
                baseText_en = stationData.departureLEDContent_en || '';
            } else {
                baseText = settings.departureLEDContent;
                baseText_en = settings.departureLEDContent_en || '';
            }
        }

        // 获取多语言文本数组和语言配置
        const multiLangTexts = this.getMultiLanguageTexts(baseText, baseText_en, stationName, currentDirection, stationData, 'station', stationIndex);
        const languageConfig = this.getLanguageConfig();
        
        // 定义节目类型（用于所有屏幕尺寸）
        const programType = isArrival ? '进站' : '出站';

        // 根据屏幕尺寸选择不同的显示策略
        if (this.isTwoRowDisplayScreen()) {
            // 两行显示屏幕：根据语言配置选择发送策略
            
            // 停止可能存在的轮播
            this.stopCarousel();
            
            if (languageConfig.isMultiLanguage && multiLangTexts.length >= 2) {
                // 多语言模式：根据语言数量选择传输方式
                
                // 保存当前站点信息（保存前两种语言用于兼容性）
                this.currentStationChinese = multiLangTexts[0] || '';
                this.currentStationEnglish = multiLangTexts[1] || '';
                
                // 检查是否需要重建节目（清屏后）
                if (this.programCleared) {
                    console.log(`检测到清屏标志，重建${programType}完整节目（多语言模式）`);
                    if (multiLangTexts.length === 2) {
                        // 2种语言：初始化双语节目
                        const chineseText = multiLangTexts[0] || '';
                        const englishText = multiLangTexts[1] || '';
                        await this.initializeStationProgram(chineseText, englishText, programType);
                        console.log(`两行显示屏幕${programType}完整节目初始化完成（双语模式），已保存站点信息供服务语使用`);
                    } else {
                        // 3-5种语言：拼接后初始化节目
                        const firstLang = multiLangTexts[0] || '';
                        const restLangs = multiLangTexts.slice(1);
                        const tmp = restLangs.filter(t => !!t && t.trim()).map(t => ({ language: 'other', code: 'x', text: t.trim(), fieldName: '' }));
                        const section2Combined = this.concatenateServiceTexts(tmp);
                        await this.initializeStationProgram(firstLang, section2Combined, programType);
                        console.log(`两行显示屏幕${programType}完整节目初始化完成（${multiLangTexts.length}语言模式），已保存站点信息供服务语使用`);
                    }
                } else {
                    // 节目已存在，使用实时刷新
                    if (multiLangTexts.length === 2) {
                        // 2种语言：使用原有的双语方法
                        const chineseText = multiLangTexts[0] || '';
                        const englishText = multiLangTexts[1] || '';
                        await this.sendDualLanguageProgram(chineseText, englishText, programType);
                        console.log(`两行显示屏幕${programType}信息发送完成（双语模式），已保存站点信息供服务语使用`);
                    } else {
                        // 3-5种语言：使用新的多语言方法
                        await this.sendMultiLanguageProgram(multiLangTexts, programType);
                        console.log(`两行显示屏幕${programType}信息发送完成（${multiLangTexts.length}语言模式），已保存站点信息供服务语使用`);
                    }
                }
            } else {
                // 单语言模式：只发送主要语言到分区1
                const mainText = multiLangTexts[0] || '';
                
                // 保存当前站点信息（单语言模式）
                this.currentStationChinese = mainText;
                this.currentStationEnglish = ''; // 单语言模式下清空英文
                
                // 检查是否需要重建节目（清屏后）
                if (this.programCleared) {
                    console.log(`检测到清屏标志，重建${programType}完整节目（单语言模式）`);
                    // 单语言模式：分区1显示进站/出站内容，分区2显示欢迎信息
                    const welcomeText = this.getWelcomeText();
                    await this.initializeStationProgram(mainText, welcomeText, '欢迎');
                    console.log(`两行显示屏幕${programType}完整节目初始化完成（单语言模式），分区1: ${mainText}, 分区2: ${welcomeText}`);
                } else {
                    // 节目已存在，只刷新分区1
                    await this.refreshSection1Only(mainText, programType);
                    console.log(`两行显示屏幕${programType}信息发送完成（单语言模式），已保存站点信息供服务语使用`);
                }
            }
        } else if (this.screenParams.height === 16 || this.screenParams.height === 20) {
            // 16/20点阵屏幕：有基础节目时走实时刷新，无基础节目时重建完整节目
            if (languageConfig.isMultiLanguage && multiLangTexts.length >= 2) {
                // 多语言模式：智能格式化中英文内容
                const chineseText = multiLangTexts[0] || '';
                const englishText = multiLangTexts[1] || '';
                const formattedText = this.smartFormatMultiLanguageText(chineseText, englishText);
                
                // 停止轮播；仅在无基础节目时发送完整节目
                this.stopCarousel();
                if (this.isSpecial32x16Screen()) {
                    await this.sendSpecial32x16Content(formattedText, programType);
                } else if (this.programCleared) {
                    await this.sendSmallScreenFullProgram(formattedText, programType);
                } else {
                    await this.refreshSmallScreenText(formattedText, programType);
                }
            } else {
                // 单语言模式：直接显示主要语言
                if (multiLangTexts.length > 0) {
                    const mainText = multiLangTexts[0] || '';
                    this.stopCarousel();
                    if (this.isSpecial32x16Screen()) {
                        await this.sendSpecial32x16Content(mainText, programType);
                    } else if (this.programCleared) {
                        await this.sendSmallScreenFullProgram(mainText, programType);
                    } else {
                        await this.refreshSmallScreenText(mainText, programType);
                    }
                }
            }
        } else {
            // 其他尺寸屏幕：使用完整节目（暂时只显示中文）
            const stationText = multiLangTexts[0] || '';
            await this.sendStationProgram(stationText, this.serviceText);
        }
    }

    // 发送欢迎信息（首次连接验证）
    async sendWelcomeMessage(currentDirection = 'up') {
        if (!this.isConnected) {
            throw new Error('设备未连接');
        }

        // 停止可能存在的轮播
        this.stopCarousel();

        // 从配置中读取欢迎LED内容
        let welcomeText = '欢迎乘坐本车';
        let welcomeText_en = 'Welcome Aboard!';
        
        if (this.configData && this.configData.ledSettings) {
            if (this.configData.ledSettings.welcomeLEDContent) {
                welcomeText = this.replaceLEDPlaceholders(this.configData.ledSettings.welcomeLEDContent, '', currentDirection);
            }
            if (this.configData.ledSettings.welcomeLEDContent_en) {
                welcomeText_en = this.replaceLEDPlaceholders(this.configData.ledSettings.welcomeLEDContent_en, '', currentDirection);
            }
        }

        // 获取多语言欢迎文本和语言配置
        const multiLangTexts = this.getMultiLanguageTexts(welcomeText, welcomeText_en, '', currentDirection, null, 'welcome');
        const languageConfig = this.getLanguageConfig();

        // 根据屏幕尺寸选择不同的显示策略
        if (this.isTwoRowDisplayScreen()) {
            // 两行显示屏幕：根据语言配置选择初始化策略
            this.stopCarousel();
            
            if (languageConfig.isMultiLanguage && multiLangTexts.length >= 2) {
                // 多语言模式：创建双语基础节目
                const chineseText = multiLangTexts[0] || welcomeText;
                const englishText = multiLangTexts[1] || welcomeText_en;
                
                // 保存欢迎信息的中英文内容，供服务语时使用
                this.currentStationChinese = chineseText;
                this.currentStationEnglish = englishText;
                
                // 首次连接时使用完整节目创建，为后续实时刷新提供基础
                await this.initializeWelcomeProgramMultiLang(chineseText, englishText);
                console.log(`两行显示屏幕欢迎节目初始化完成（多语言模式），已保存欢迎信息供服务语使用`);
            } else {
                // 单语言模式：创建单语言基础节目（定义两分区），以便后续rt_show在64x64中部渲染
                const welcomeMainText = multiLangTexts[0] || welcomeText;
                
                // 保存欢迎信息（单语言模式）
                this.currentStationChinese = welcomeMainText;
                this.currentStationEnglish = ''; // 单语言模式下清空英文
                
                // 对两行显示屏幕，初始化基础欢迎节目（包含分区定义）
                await this.initializeWelcomeProgram(welcomeMainText);
                console.log(`两行显示屏幕欢迎节目初始化完成（单语言模式），已保存欢迎信息供服务语使用`);
            }
        } else if (this.screenParams.height === 16 || this.screenParams.height === 20) {
            // 16/20点阵屏幕：有基础节目时走实时刷新，无基础节目时重建完整节目
            if (languageConfig.isMultiLanguage && multiLangTexts.length >= 2) {
                // 多语言模式：智能格式化中英文欢迎信息
                const welcomeChineseText = multiLangTexts[0] || welcomeText;
                const welcomeEnglishText = multiLangTexts[1] || welcomeText_en;
                const formattedWelcomeText = this.smartFormatMultiLanguageText(welcomeChineseText, welcomeEnglishText);
                
                // 停止轮播；仅在无基础节目时发送完整节目
                this.stopCarousel();
                if (this.isSpecial32x16Screen()) {
                    await this.sendSpecial32x16Content(formattedWelcomeText, '欢迎');
                } else if (this.programCleared) {
                    await this.sendSmallScreenFullProgram(formattedWelcomeText, '欢迎');
                } else {
                    await this.refreshSmallScreenText(formattedWelcomeText, '欢迎');
                }
            } else {
                // 单语言模式：直接显示主要语言的欢迎信息
                if (multiLangTexts.length > 0) {
                    const mainWelcomeText = multiLangTexts[0] || welcomeText;
                    this.stopCarousel();
                    if (this.isSpecial32x16Screen()) {
                        await this.sendSpecial32x16Content(mainWelcomeText, '欢迎');
                    } else if (this.programCleared) {
                        await this.sendSmallScreenFullProgram(mainWelcomeText, '欢迎');
                    } else {
                        await this.refreshSmallScreenText(mainWelcomeText, '欢迎');
                    }
                }
            }
        } else {
            // 其他尺寸屏幕：发送专门的欢迎节目（暂时只显示中文）
            await this.sendWelcomeProgram(multiLangTexts[0] || welcomeText);
        }
    }

    // 初始化两行显示屏幕的完整进站/出站节目（清屏后重建节目结构）
    async initializeStationProgram(section1Text, section2Text = '', programType = 'station') {
        if (!this.isConnected) return;
        
        // 获取LED配置
        const ledSettings = this.configData?.ledSettings || {};
        const stationColor = parseInt(ledSettings.stationTextColor || "255");
        const serviceColor = parseInt(ledSettings.serviceTextColor || "65280");
        
        // 检查是否为服务语消息（决定分区2使用什么颜色）
        const isServiceMessage = programType === 'service' || (typeof programType === 'string' && programType.includes('服务语'));
        
        // 更新内部状态
        this.stationText = section1Text;
        this.section2Text = section2Text;
        
        const cfg = this.getTwoRowRegionConfig();

        const program = {
            id_pro: 128,
            property_pro: {
                type_pro: 1,
                show_now: 1,
                play_loop: 65535,
                width: this.screenParams.width,
                height: this.screenParams.height
            },
            list_region: [
                {
                    info_pos: {
                        x: 0,
                        y: cfg.topOffset,
                        w: cfg.regionWidth,
                        h: cfg.rowHeight
                    },
                    list_item: [{
                        type_item: "text",
                        text: section1Text,
                        size: 16,
                        color: stationColor,
                        align_horizontal: 'center',
                        align_vertical: 'center',
                        ...this.getAnimationSettings(section1Text, true)
                    }]
                },
                {
                    info_pos: {
                        x: 0,
                        y: cfg.topOffset + cfg.rowHeight,
                        w: cfg.regionWidth,
                        h: cfg.rowHeight
                    },
                    list_item: [{
                        type_item: "text",
                        text: section2Text || '', // 如果没有第二语言，显示空白
                        size: 16,
                        color: isServiceMessage ? serviceColor : stationColor,
                        align_horizontal: 'center',
                        align_vertical: 'center',
                        ...this.getAnimationSettings(section2Text || '', false)
                    }]
                }
            ]
        };

        const cmd = {
            pkts_program: program,
            sno: this.generateSno()
        };

        console.log(`初始化${programType}完整节目（清屏后重建）:`, JSON.stringify(cmd, null, 2));
        const success = await this.sendCommand(cmd, null, `正在初始化${programType}节目...`);
        if (success) {
            console.log(`${programType}完整节目初始化成功，节目结构已重建`);
            // 重建完成后，清除programCleared标志
            this.programCleared = false;
        }
    }

    // 初始化两行显示屏幕的多语言欢迎节目（160*32或64*64中部32高区域；分区1中文，分区2英文）
    async initializeWelcomeProgramMultiLang(chineseText, englishText) {
        if (!this.isConnected) return;
        
        // 获取LED配置
        const ledSettings = this.configData?.ledSettings || {};
        const stationColor = parseInt(ledSettings.stationTextColor || "255");
        const serviceColor = parseInt(ledSettings.serviceTextColor || "65280");
        
        // 存储分区2的内容
        this.section2Text = englishText;
        
        const cfg = this.getTwoRowRegionConfig();

        const program = {
            id_pro: 128,
            property_pro: {
                type_pro: 1,
                show_now: 1,
                play_loop: 65535,
                width: this.screenParams.width,
                height: this.screenParams.height
            },
            list_region: [
                {
                    info_pos: {
                        x: 0,
                        y: cfg.topOffset,
                        w: cfg.regionWidth,
                        h: cfg.rowHeight
                    },
                    list_item: [{
                        type_item: "text",
                        text: chineseText,
                        size: 16,
                        color: stationColor,
                        align_horizontal: 'center',
                        align_vertical: 'center',
                        ...this.getWelcomeAnimationSettings(chineseText)
                    }]
                },
                {
                    info_pos: {
                        x: 0,
                        y: cfg.topOffset + cfg.rowHeight,
                        w: cfg.regionWidth,
                        h: cfg.rowHeight
                    },
                    list_item: [{
                        type_item: "text",
                        text: englishText,
                        size: 16,
                        color: serviceColor,
                        align_horizontal: 'center',
                        align_vertical: 'center',
                        ...this.getWelcomeAnimationSettings(englishText)
                    }]
                }
            ]
        };

        const cmd = {
            pkts_program: program,
            sno: this.generateSno()
        };

        const success = await this.sendCommand(cmd, null, '正在发送多语言欢迎节目...');
        if (success) {
            console.log('多语言欢迎节目发送成功');
            // 节目创建成功后，清除programCleared标志
            this.programCleared = false;
        }
    }

    // 发送专门的欢迎节目（用于非160*32屏幕）
    async sendWelcomeProgram(welcomeText) {
        if (!this.isConnected) return;
        
        // 获取LED配置
        const ledSettings = this.configData?.ledSettings || {};
        const stationColor = parseInt(ledSettings.stationTextColor || "255");
        
        const program = {
            id_pro: 128,
            property_pro: {
                type_pro: 1,
                show_now: 1,
                play_loop: 65535,
                width: this.screenParams.width,
                height: this.screenParams.height
            }
        };
        
        if (this.screenParams.width === 96 && this.screenParams.height === 20) {
            program.list_region = [
                {
                    info_pos: {
                        x: 0,
                        y: 0,
                        w: 96,
                        h: 20
                    },
                    list_item: [{
                        type_item: "text",
                        text: welcomeText,
                        size: 16,
                        color: stationColor,
                        align_horizontal: 'center',
                        align_vertical: 'center',
                        ...this.getWelcomeAnimationSettings(welcomeText)
                    }]
                }
            ];
        } else if (this.screenParams.height === 16) {
            program.list_region = [
                {
                    info_pos: {
                        x: 0,
                        y: 0,
                        w: this.screenParams.width,
                        h: 16
                    },
                    list_item: [{
                        type_item: "text",
                        text: welcomeText,
                        size: 16,
                        color: stationColor,
                        align_horizontal: 'center',
                        align_vertical: 'center',
                        ...this.getWelcomeAnimationSettings(welcomeText)
                    }]
                }
            ];
        } else {
            // 其他尺寸屏幕，只显示欢迎信息在第一行
            program.list_region = [
                {
                    info_pos: {
                        x: 0,
                        y: 0,
                        w: this.screenParams.width,
                        h: 16
                    },
                    list_item: [{
                        type_item: "text",
                        text: welcomeText,
                        size: 16,
                        color: stationColor,
                        align_horizontal: 'center',
                        align_vertical: 'center',
                        ...this.getWelcomeAnimationSettings(welcomeText)
                    }]
                }
            ];
        }

        const cmd = {
            pkts_program: program,
            sno: this.generateSno()
        };

        const success = await this.sendCommand(cmd, null, '正在发送欢迎节目...');
        if (success) {
            console.log('欢迎节目发送成功');
            // 节目创建成功后，清除programCleared标志
            this.programCleared = false;
        }
    }

    async sendSmallScreenFullProgram(text, programType = '欢迎') {
        if (!this.isConnected) return;
        if (!(this.screenParams.height === 16 || this.screenParams.height === 20)) {
            throw new Error('sendSmallScreenFullProgram仅适用于16/20点阵屏幕');
        }

        const displayText = text || '';
        const isStation = !(programType === 'service' || (typeof programType === 'string' && programType.includes('服务语')));
        const formattedText = this.formatTextForDisplay(displayText, isStation);
        const color = this.getColorByProgramType(programType);
        const isSpecial32x16 = this.isSpecial32x16Screen();
        const { leftSpeed } = this.getScrollSpeeds();
        const shouldForceInitialWelcomeScroll = programType === '欢迎' && isSpecial32x16 && this.forceInitial32x16WelcomeScroll;
        const itemAnimation = programType === '欢迎'
            ? (shouldForceInitialWelcomeScroll ? {
                info_animate: {
                    model_continue: "left",
                    speed: leftSpeed
                }
            } : {})
            : this.getAnimationSettings(displayText, isStation);
        const programId = isSpecial32x16 ? 1 : 128;

        const program = {
            id_pro: programId,
            ...(isSpecial32x16 ? { data_save: 1 } : {}),
            property_pro: {
                type_pro: isSpecial32x16 ? 0 : 1,
                show_now: 1,
                play_loop: 65535,
                width: this.screenParams.width,
                height: this.screenParams.height
            },
            list_region: [
                {
                    info_pos: {
                        x: 0,
                        y: 0,
                        w: this.screenParams.width,
                        h: this.screenParams.height
                    },
                    list_item: [{
                        type_item: "text",
                        text: formattedText,
                        size: 16,
                        color: color,
                        align_horizontal: 'center',
                        align_vertical: 'center',
                        ...itemAnimation
                    }]
                }
            ]
        };

        this.stationText = formattedText;

        const cmd = {
            pkts_program: program,
            sno: this.generateSno()
        };

        const success = await this.sendCommand(cmd, null, `正在发送小屏幕${programType}完整节目...`);
        if (success) {
            if (shouldForceInitialWelcomeScroll) {
                this.forceInitial32x16WelcomeScroll = false;
            }
            this.programCleared = false;
        }
    }

    // 发送服务语信息（新的多语言连接工作流）
    async sendServiceMessage(serviceNumber, stationName = '', currentDirection = 'up', stationData = null) {
        if (!this.isConnected) {
            throw new Error('设备未连接');
        }

        // 停止可能存在的轮播
        this.stopCarousel();

        // 从配置中读取服务语LED内容
        if (!this.configData || !this.configData.ledSettings) {
            throw new Error('未找到LED配置，请先上传包含LED配置的压缩包');
        }

        console.log(`开始处理服务语${serviceNumber}的多语言发送...`);
        console.log(`当前站点: ${stationName}, 当前方向: ${currentDirection}`);
        
        // 第一步：获取多语言配置信息
        const languageConfig = this.getLanguageConfig();
        console.log(`语言配置:`, languageConfig);
        
        // 第二步：获取该服务语的所有已定义语言文本（传递站点和方向信息）
        const availableTexts = this.getServiceLanguageTexts(serviceNumber, languageConfig, stationName, currentDirection, stationData);
        console.log(`服务语${serviceNumber}找到${availableTexts.length}种语言的内容:`, availableTexts);
        
        if (availableTexts.length === 0) {
            throw new Error(`服务语${serviceNumber}没有找到任何语言内容`);
        }
        
        // 第三步：根据滚动逻辑连接文本
        const concatenatedText = this.concatenateServiceTexts(availableTexts);
        console.log(`连接后的服务语${serviceNumber}文本: ${concatenatedText}`);
        console.log(`屏幕尺寸: ${this.screenParams.width}x${this.screenParams.height}`);

        // 第四步：根据屏幕尺寸发送连接后的文本
        if (this.screenParams.height === 16 || this.screenParams.height === 20) {
            // 16/20点阵屏幕：有基础节目时走实时刷新，无基础节目时重建完整节目
            if (this.isSpecial32x16Screen()) {
                await this.sendSpecial32x16Content(concatenatedText, `服务语${serviceNumber}`);
            } else if (this.programCleared) {
                await this.sendSmallScreenFullProgram(concatenatedText, `服务语${serviceNumber}`);
            } else {
                await this.refreshSmallScreenText(concatenatedText, `服务语${serviceNumber}`);
            }
            console.log(`16/20点阵屏幕服务语${serviceNumber}发送完成`);
        } else if (this.isTwoRowDisplayScreen()) {
            // 两行显示屏幕：发送连接后的文本到分区2，分区1显示站点或欢迎信息
            
            // 检查是否需要重建节目（清屏后）
            if (this.programCleared) {
                console.log(`检测到清屏标志，重建服务语${serviceNumber}完整节目`);
                
                // 判断语言模式
                if (languageConfig.isMultiLanguage && availableTexts.length >= 2) {
                    // 多语言模式：获取当前站点的多语言内容作为分区1
                    const multiLanguageStationText = this.getCurrentStationMultiLanguageText();
                    console.log(`多语言模式 - 分区1: ${multiLanguageStationText}, 分区2: ${concatenatedText}`);
                    await this.initializeStationProgram(multiLanguageStationText, concatenatedText, `服务语${serviceNumber}`);
                    console.log(`两行显示屏幕服务语${serviceNumber}完整节目初始化完成（多语言模式）`);
                } else {
                    // 单语言模式：分区1显示欢迎信息，分区2显示服务语
                    const welcomeText = this.getWelcomeText();
                    console.log(`单语言模式 - 分区1: ${welcomeText}, 分区2: ${concatenatedText}`);
                    await this.initializeStationProgram(welcomeText, concatenatedText, `服务语${serviceNumber}`);
                    console.log(`两行显示屏幕服务语${serviceNumber}完整节目初始化完成（单语言模式）`);
                }
            } else {
                // 节目已存在，使用实时刷新
                // 获取当前站点的多语言内容
                const multiLanguageStationText = this.getCurrentStationMultiLanguageText();
                console.log(`获取到的多语言站点内容: ${multiLanguageStationText}`);
                
                // 使用双语发送：分区1显示多语言站点，分区2显示连接后的服务语
                await this.sendDualLanguageProgram(multiLanguageStationText, concatenatedText, `服务语${serviceNumber}`);
                console.log(`两行显示屏幕服务语${serviceNumber}发送完成，分区1: ${multiLanguageStationText}, 分区2: ${concatenatedText}`);
            }
        } else {
            // 其他尺寸屏幕：发送连接后的文本
            await this.refreshSection2Only(concatenatedText);
            console.log(`其他尺寸屏幕服务语${serviceNumber}发送完成`);
        }
    }



    // 获取欢迎文本（用于清屏后重建节目）
    getWelcomeText() {
        let welcomeText = '欢迎乘坐本车';
        
        if (this.configData && this.configData.ledSettings && this.configData.ledSettings.welcomeLEDContent) {
            welcomeText = this.replaceLEDPlaceholders(this.configData.ledSettings.welcomeLEDContent, '', 'up');
        }
        
        return welcomeText;
    }

    // 根据程序类型确定正确的颜色
    getColorByProgramType(programType) {
        // 获取LED配置
        const ledSettings = this.configData?.ledSettings || {};
        const stationColor = parseInt(ledSettings.stationTextColor || "255");
        const serviceColor = parseInt(ledSettings.serviceTextColor || "65280");
        
        // 检查是否为服务语消息
        const isServiceMessage = programType === 'service' || (typeof programType === 'string' && programType.includes('服务语'));
        
        console.log(`颜色选择 - 程序类型: ${programType}, 是否服务语: ${isServiceMessage}`);
        console.log(`配置的站名颜色: ${stationColor}, 服务语颜色: ${serviceColor}`);
        
        return isServiceMessage ? serviceColor : stationColor;
    }
    
    // 计算文字宽度
    calculateTextWidth(text, fontSize = 16) {
        if (!text) return 0;
        
        let width = 0;
        for (let i = 0; i < text.length; i++) {
            // 判断是否为全角字符
            if (text.charCodeAt(i) > 127 || text.charCodeAt(i) === 94) {
                width += fontSize; // 全角字符占据fontSize宽度
            } else {
                width += fontSize / 2; // 半角字符占据fontSize/2宽度
            }
        }
        return width;
    }

    // 检查单行文本是否超过屏幕宽度限制（用于小屏幕优化）
    checkLineExceedsWidth(text, fontSize = 16) {
        if (!text) return false;
        
        // 为小屏幕使用字符数量计算而不是像素宽度
        let maxChars;
        if (this.screenParams.width === 96 && this.screenParams.height === 20) {
            // 96*20屏幕，大约可以显示6个中文字符
            maxChars = 6;
        } else if (this.screenParams.height === 16) {
            // 16点阵屏幕，根据实际宽度估算可显示的字符数
            maxChars = Math.floor(this.screenParams.width / 16); // 每个中文字符大约16像素
        } else {
            // 其他屏幕使用像素宽度计算
            const screenWidth = this.screenParams.width;
            const textWidth = this.calculateTextWidth(text, fontSize);
            const maxWidth = screenWidth * 0.9;
            return textWidth > maxWidth;
        }
        
        // 计算实际字符数（全角字符计为1，半角字符计为0.5）
        let charCount = 0;
        for (let i = 0; i < text.length; i++) {
            if (text.charCodeAt(i) > 127) {
                charCount += 1; // 全角字符（中文等）
            } else {
                charCount += 0.5; // 半角字符（英文数字等）
            }
        }
        
        console.log(`文本: "${text}", 字符计数: ${charCount}, 最大字符数: ${maxChars}, 超出: ${charCount > maxChars}`);
        return charCount > maxChars;
    }

    // 检查多行文本是否有任何一行超过宽度限制
    checkAnyLineExceedsWidth(text, fontSize = 16) {
        if (!text) return false;
        
        const lines = text.split('\n');
        for (const line of lines) {
            if (this.checkLineExceedsWidth(line.trim(), fontSize)) {
                return true;
            }
        }
        return false;
    }

    // 智能格式化多语言文本（适用于小屏幕）
    smartFormatMultiLanguageText(chineseText, englishText, fontSize = 16) {
        if (!chineseText) return '';
        if (!englishText) return chineseText;
        
        // 只对小屏幕进行智能格式化
        if (!(this.screenParams.height === 16 || this.screenParams.height === 20)) {
            return `${chineseText}\n${englishText}`;
        }
        
        // 分别检查中文和英文行是否超过宽度限制
        const chineseExceeds = this.checkLineExceedsWidth(chineseText, fontSize);
        const englishExceeds = this.checkLineExceedsWidth(englishText, fontSize);
        
        console.log(`小屏幕宽度检查 - 中文: "${chineseText}" 超出: ${chineseExceeds}, 英文: "${englishText}" 超出: ${englishExceeds}`);
        
        if (!chineseExceeds && !englishExceeds) {
            // 如果中英文都不超过限制，使用换行符分隔（静态显示）
            console.log('小屏幕多语言格式：使用换行符分隔（静态显示）');
            return `${chineseText}\n${englishText}`;
        } else {
            // 如果任意一行超过限制，使用空格分隔并启用滚动
            const spaceFormatted = `${chineseText} ${englishText}`;
            console.log('小屏幕多语言格式：使用空格分隔（滚动显示）');
            return spaceFormatted;
        }
    }
    
    // 判断文字是否需要滚动
    needsScrolling(text, fontSize, containerWidth) {
        const textWidth = this.calculateTextWidth(text, fontSize);
        return textWidth > containerWidth;
    }

    // 新增：兼容读取滚动速度
    getScrollSpeeds() {
        // 兼容读取：优先读取新字段，若不存在则回退至旧字段
        const ledSettings = this.configData?.ledSettings || {};
        const leftSpeed = parseInt(
            (ledSettings.leftScrollSpeed != null ? ledSettings.leftScrollSpeed : ledSettings.stationScrollSpeed) || '10'
        );
        const upSpeed = parseInt(
            (ledSettings.upScrollSpeed != null ? ledSettings.upScrollSpeed : ledSettings.serviceScrollSpeed) || '5'
        );
        return { leftSpeed, upSpeed };
    }

    // 欢迎文本的动画设置：超长滚动，速度取 leftScrollSpeed
    getWelcomeAnimationSettings(text) {
        // 计算最大显示字符数
        let maxChars;
        if (this.screenParams.width === 96 && this.screenParams.height === 20) {
            maxChars = 6; // 96*20最多可以显示6个字
        } else {
            // 其他尺寸（含128x32、160x32等）按屏宽计算
            maxChars = Math.floor(this.screenParams.width / 16);
        }

        // 计算实际字符数（全角字符计1，半角字符计0.5）
        let charCount = 0;
        for (let i = 0; i < text.length; i++) {
            if (text.charCodeAt(i) > 127) {
                charCount += 1; // 全角字符
            } else {
                charCount += 0.5; // 半角字符
            }
        }

        console.log(`欢迎文本: "${text}", 字符计数: ${charCount}, 最大字符数: ${maxChars}`);

        // 根据字符数量选择显示模式
        if (charCount <= maxChars) {
            // 字符数不超过屏幕宽度，保持静止显示
            console.log(`欢迎文本字符数未超过限制，使用静止显示`);
            return {}; // 不使用动画，静态显示
        } else {
            // 字符数超过屏幕宽度，使用连续向左滚动
            console.log(`欢迎文本字符数超过限制，使用连续向左滚动`);
            const { leftSpeed } = this.getScrollSpeeds();
            return {
                info_animate: {
                    model_continue: "left",
                    speed: leftSpeed
                }
            };
        }
    }

    // ... (rest of the code remains the same)

    // 检查文本是否需要滚动（基于原始换行格式）
    checkIfTextNeedsScrolling(text, isStation = true) {
        if (!this.configData?.ledSettings) {
            return true; // 如果没有配置，默认需要滚动
        }

        // 计算最大显示字符数
        let maxChars;
        if (this.screenParams.width === 96 && this.screenParams.height === 20) {
            maxChars = 6; // 96*20最多可以显示6个字
        } else {
            // 其他尺寸（含128x32、160x32等）按屏宽计算
            maxChars = Math.floor(this.screenParams.width / 16);
        }

        // 检查文本是否包含换行符
        const lines = text.split('\n');
        let needsScrolling = false;

        // 分别检查每一行的字符数
        lines.forEach((line, index) => {
            // 计算当前行的实际字符数（全角字符计1，半角字符计0.5）
            let charCount = 0;
            for (let i = 0; i < line.length; i++) {
                if (line.charCodeAt(i) > 127) {
                    charCount += 1; // 全角字符
                } else {
                    charCount += 0.5; // 半角字符
                }
            }

            console.log(`第${index + 1}行: "${line}", 字符计数: ${charCount}, 最大字符数: ${maxChars}`);

            // 如果任何一行超过最大字符数，则需要滚动
            if (charCount > maxChars) {
                needsScrolling = true;
            }
        });

        console.log(`文本: "${text}", 是否需要滚动: ${needsScrolling}`);
        return needsScrolling;
    }

    // 获取动画设置（智能滚动控制）
    getAnimationSettings(text, isStation = true) {
        // 如果文本为空或未定义，返回静态显示（不使用动画）
        if (!text || text.trim() === '') {
            console.log('文本为空，返回静态显示（无动画）');
            return {};
        }
        
        if (!this.configData?.ledSettings) {
            // 如果没有配置，返回默认的左滚动
            return {
                info_animate: {
                    model_continue: "left",
                    speed: 10
                }
            };
        }

        const ledSettings = this.configData.ledSettings;

        // 使用原始文本判断是否需要滚动
        let needsScrolling;
        if (text.includes('\n')) {
            // 对于包含换行符的文本，使用checkIfTextNeedsScrolling方法
            needsScrolling = this.checkIfTextNeedsScrolling(text, isStation);
        } else {
            // 对于单行文本，直接使用原有逻辑
            const maxChars = this.screenParams.width === 96 && this.screenParams.height === 20 ? 6 :
                            Math.floor(this.screenParams.width / 16);

            let charCount = 0;
            for (let i = 0; i < text.length; i++) {
                if (text.charCodeAt(i) > 127) {
                    charCount += 1; // 全角字符
                } else {
                    charCount += 0.5; // 半角字符
                }
            }

            needsScrolling = charCount > maxChars;
            console.log(`单行文本: "${text}", 字符计数: ${charCount}, 最大字符数: ${maxChars}, 需要滚动: ${needsScrolling}`);
        }

        // 检查是否配置了NoScrollMode为left
        const noScrollMode = isStation ?
            (ledSettings.stationNoScrollMode || "center") :
            (ledSettings.serviceNoScrollMode || "center");

        // 如果noScrollMode为left，无论内容长度都强制使用scrollmode的连续向左滚动逻辑
        if (noScrollMode === "left") {
            const { leftSpeed } = this.getScrollSpeeds();
            console.log(`NoScrollMode为left，强制使用连续向左滚动，速度: ${leftSpeed}`);
            return {
                info_animate: {
                    model_continue: "left",
                    speed: leftSpeed
                }
            };
        }

        // 根据是否需要滚动选择显示模式
        if (!needsScrolling) {
            console.log(`文本未超过限制，使用NoScrollMode: ${noScrollMode}`);

            // 处理不同的noScrollMode值
            if (noScrollMode === "top") {
                const { upSpeed } = this.getScrollSpeeds();
                return {
                    info_animate: {
                        model_continue: "top",
                        speed: upSpeed
                    }
                };
            } else if (noScrollMode === "bottom") {
                const { upSpeed } = this.getScrollSpeeds();
                return {
                    info_animate: {
                        model_continue: "down",
                        speed: upSpeed
                    }
                };
            } else {
                // "center"或其他值：居中静态显示，不使用动画
                console.log(`居中静态显示，不使用动画`);
                return {};
            }
        } else {
            // 有行超过屏幕宽度，必须使用ScrollMode进行滚动
            const scrollMode = isStation ?
                (ledSettings.stationScrollMode || "left") :
                (ledSettings.serviceScrollMode || "left");
            const { leftSpeed } = this.getScrollSpeeds();

            console.log(`文本超过限制，强制使用ScrollMode: ${scrollMode}, 速度: ${leftSpeed}`);

            return {
                info_animate: {
                    model_continue: scrollMode,
                    speed: leftSpeed
                }
            };
        }
    }

    // 根据滚动设置格式化文本显示（保留换行或将其替换为空格用于滚动）
    formatTextForDisplay(text, isStation = true) {
        if (text == null) return '';
        // 单行文本原样返回
        if (!text.includes('\n')) return text;

        const ledSettings = this.configData?.ledSettings || {};
        const noScrollMode = isStation
            ? (ledSettings.stationNoScrollMode || 'center')
            : (ledSettings.serviceNoScrollMode || 'center');

        // 如果NoScrollMode是left，强制横向连续滚动，格式化为单行
        if (noScrollMode === 'left') {
            const formatted = text.split('\n').join('  ');
            console.log(`NoScrollMode=left，合并多行为单行: "${text}" -> "${formatted}"`);
            return formatted;
        }

        // 智能判断是否需要滚动；需要滚动则合并为单行
        const needs = this.checkIfTextNeedsScrolling(text, isStation);
        if (needs) {
            const formatted = text.split('\n').join('  ');
            console.log(`需要滚动，合并多行为单行: "${text}" -> "${formatted}"`);
            return formatted;
        }

        // 不需要滚动则保持换行显示
        console.log(`不需要滚动，保持换行: "${text}"`);
        return text;
    }


}

// 导出 LEDController
if (typeof window !== 'undefined') {
    window.LEDController = LEDController;
}
if (typeof module !== 'undefined' && module.exports) {
    module.exports = LEDController;
} 
